import { EvidenceCardConfig } from "@risalabs_frontend_org/oasis-ui-kit";
import { colors } from "../../../constants/colors";
import {
  MedicalQuestion,
  QuestionOption,
} from "../../../data-model/medicalQuestion";

export type AnswerMap = Record<string, string | string[]>;

export interface AnsweredMedicalQuestion extends Omit<
  MedicalQuestion,
  "children"
> {
  children: Record<string, AnsweredMedicalQuestion[]>;
}

/**
 * Recursively collects all descendant question IDs from a list of questions,
 * used to clear stale answers when a parent selection changes.
 */
export const collectDescendantIds = (
  questions: MedicalQuestion[],
): string[] => {
  const ids: string[] = [];
  const traverse = (q: MedicalQuestion) => {
    ids.push(q.id);
    Object.values(q.children).forEach((children) => children.forEach(traverse));
  };
  questions.forEach(traverse);
  return ids;
};

/**
 * Walks the question tree and extracts pre-filled answers into a flat map.
 */
export const initializeAnswers = (questions: MedicalQuestion[]): AnswerMap => {
  const answers: AnswerMap = {};
  const traverse = (q: MedicalQuestion) => {
    if (q.answer) {
      const answerArr = Array.isArray(q.answer) ? q.answer : [q.answer];
      if (q.question_type === "multi_correct") {
        answers[q.id] = answerArr.map((opt) => opt.id);
      } else if (q.question_type === "subjective") {
        answers[q.id] = answerArr[0]?.text ?? "";
      } else {
        answers[q.id] = answerArr[0]?.id ?? "";
      }
      for (const opt of answerArr) {
        if (q.children[opt.id]) {
          q.children[opt.id].forEach(traverse);
        }
      }
    }
  };
  questions.forEach(traverse);
  return answers;
};

export const buildEvidenceCards = (
  facts: MedicalQuestion["facts"],
): EvidenceCardConfig[] | undefined => {
  if (!facts) return undefined;
  const cards: EvidenceCardConfig[] = [];
  if (facts.supportive_facts.length > 0) {
    cards.push({
      title: "Supportive Facts",
      statements: facts.supportive_facts,
      cardBackGroundColor: colors.primaryGreen[11],
    });
  }
  if (facts.contradictory_facts.length > 0) {
    cards.push({
      title: "Contradictory Facts",
      statements: facts.contradictory_facts,
      cardBackGroundColor: colors.secondaryYellow.contradictory,
    });
  }
  return cards.length > 0 ? cards : undefined;
};

/**
 * Produces a deep clone of the question tree with each node's `answer`
 * field populated from the current AnswerMap.
 *
 * - single_correct → `answer` = matched QuestionOption
 * - multi_correct  → `answer` = matched QuestionOption[]
 * - subjective     → `answer` = { id: questionId, text: value }
 *
 * Only the branches that correspond to the user's selections are
 * included in `children`.
 */
export const buildAnsweredQuestions = (
  questions: MedicalQuestion[],
  answers: AnswerMap,
): AnsweredMedicalQuestion[] => {
  return questions.map((q) => {
    const raw = answers[q.id];
    let answer: QuestionOption | QuestionOption[] | null = null;

    if (q.question_type === "single_correct" && typeof raw === "string") {
      answer = q.options.find((opt) => opt.id === raw) ?? null;
    } else if (q.question_type === "multi_correct" && Array.isArray(raw)) {
      answer = q.options.filter((opt) => raw.includes(opt.id));
    } else if (q.question_type === "subjective" && typeof raw === "string") {
      answer = raw ? { id: q.id, text: raw } : null;
    }

    const visibleChildren: Record<string, AnsweredMedicalQuestion[]> = {};
    const selectedIds = Array.isArray(raw) ? raw : raw ? [raw] : [];
    for (const optId of selectedIds) {
      if (q.children[optId]) {
        visibleChildren[optId] = buildAnsweredQuestions(
          q.children[optId],
          answers,
        );
      }
    }

    return {
      ...q,
      answer,
      children: visibleChildren,
    };
  });
};

/**
 * Resolves which child questions should be visible based on
 * the current answer(s) for a given question.
 */
export const getVisibleChildren = (
  question: MedicalQuestion,
  answers: AnswerMap,
): MedicalQuestion[] => {
  const answer = answers[question.id];
  if (answer === undefined || question.question_type === "subjective")
    return [];

  const selectedIds = Array.isArray(answer) ? answer : [answer];
  const result: MedicalQuestion[] = [];
  for (const id of selectedIds) {
    if (question.children[id]) {
      result.push(...question.children[id]);
    }
  }
  return result;
};
